import React, { useContext, useState } from 'react';

import { Divider } from '@mantine/core';
import {
	FGAInternalRole,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions.ts';
import { defaultToEmptyString } from '@dealstack/shared/lib/format/string.ts';
import { PermissionRole, PersonType } from '@dealstack/shared/lib/generated';
import {
	usePermissionRequest,
	PermissionWrapper,
} from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { AddressDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/PersonAddress/AddressDrawer/AddressDrawer.tsx';
import { PersonAddresses } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/PersonAddress/PersonAddresses.tsx';
import { FloatingDetailsActions } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/FloatingDetailsActions.tsx';
import { DetailsSection } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/NaturalPersonDetails/DetailsSection/DetailsSection.tsx';
import { RelatedPeopleSection } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/RelatedPeopleSection/RelatedPeopleSection.tsx';
import { RelatedPeopleDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/RelatedPeopleDrawer/RelatedPeopleDrawer.tsx';
import { BankAccountSection } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/BankAccountSection/BankAccountSection.tsx';
import { BankAccountDrawer } from '@dealstack/frontend/shared/components/BankAccountDrawer/BankAccountDrawer.tsx';
import { useCapBankAccountDrawerProps } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/hooks/useCapBankAccountDrawerProps.ts';
import { TaxInformationSection } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/TaxInformationSection/TaxInformationSection.tsx';
import { TaxInfoDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/TaxInfoDrawer/TaxInfoDrawer.tsx';
import { InvestmentProgrammeSection } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/InvestmentProgrammeSection/InvestmentProgrammeSection.tsx';
import { LinkInvestmentProgrammeDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/InvestmentProgrammeDrawer/LinkInvestmentProgrammeDrawer.tsx';
import { EmploymentRecordSection } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/EmploymentRecordSection/EmploymentRecordSection.tsx';
import { EmploymentRecordDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/EmploymentRecordDrawer/EmploymentRecordDrawer.tsx';

export const NaturalPersonDetails = () => {
	const { dealId } = useContext(DealContext) || {};
	const bankAccountDrawerProps = useCapBankAccountDrawerProps();
	const [createAddressMode, setCreateAddressMode] = useState(false);
	const [createRelatedPeopleMode, setCreateRelatedPeopleMode] = useState<boolean>(false);
	const [createBankAccountMode, setCreateBankAccountMode] = useState<boolean>(false);
	const [createTaxInfoMode, setCreateTaxInfoMode] = useState<boolean>(false);
	const [createEmploymentRecordMode, setCreateEmploymentRecordMode] = useState<boolean>(false);
	const [addPersonToMip, setAddPersonToMip] = useState<boolean>(false);

	const { hasPermission: hasSupportPermission } = usePermissionRequest({
		resourceType: ResourceType.DEAL,
		resourceId: defaultToEmptyString(dealId),
		permissionRole: FGAInternalRole.DEALSTACK_SUPPORT,
	});

	return (
		<PermissionWrapper
			resourceType={ResourceType.DEAL}
			resourceId={defaultToEmptyString(dealId)}
			permissionRole={PermissionRole.EDITOR}
		>
			{(hasPermission) => (
				<>
					<DetailsSection
						hasEditorPermission={hasPermission}
						hasSupportPermission={hasSupportPermission}
					/>

					<PersonAddresses
						hasEditorPermission={hasPermission}
						onAddAddress={() => setCreateAddressMode(true)}
					/>

					<BankAccountSection hasEditorPermission={hasPermission} />

					<TaxInformationSection hasEditorPermission={hasPermission} />

					<RelatedPeopleSection hasEditorPermission={hasPermission} />

					<EmploymentRecordSection hasEditorPermission={hasPermission} />

					<InvestmentProgrammeSection hasEditorPermission={hasPermission} />

					<Divider />

					<RelatedPeopleDrawer
						opened={createRelatedPeopleMode}
						onClose={() => setCreateRelatedPeopleMode(false)}
						selectedEntityType={PersonType.NATURAL}
						hasPermission={hasPermission}
						relationType={null}
						relatedPeople={null}
					/>

					<TaxInfoDrawer
						opened={createTaxInfoMode}
						onClose={() => setCreateTaxInfoMode(false)}
						hasPermission={hasPermission}
						taxData={null}
					/>

					<BankAccountDrawer
						{...bankAccountDrawerProps}
						opened={createBankAccountMode}
						onClose={() => setCreateBankAccountMode(false)}
						hasPermission={hasPermission}
						bankAccount={null}
					/>

					<EmploymentRecordDrawer
						opened={createEmploymentRecordMode}
						onClose={() => setCreateEmploymentRecordMode(false)}
						hasPermission={hasPermission}
						employmentRecord={null}
					/>

					<AddressDrawer
						opened={createAddressMode}
						hasPermission={hasPermission}
						onClose={() => setCreateAddressMode(false)}
						address={null}
					/>

					<LinkInvestmentProgrammeDrawer
						opened={addPersonToMip}
						onClose={() => setAddPersonToMip(false)}
						hasPermission={hasPermission}
					/>

					{hasPermission ? (
						<FloatingDetailsActions
							onAddAddress={() => setCreateAddressMode(true)}
							onAddRelatedPerson={() => setCreateRelatedPeopleMode(true)}
							onAddBankAccount={() => setCreateBankAccountMode(true)}
							onAddTaxInformation={() => setCreateTaxInfoMode(true)}
							onAddEmploymentRecord={() => setCreateEmploymentRecordMode(true)}
							onAddPersonToMip={() => setAddPersonToMip(true)}
						/>
					) : null}
				</>
			)}
		</PermissionWrapper>
	);
};
